import params from "../configs/params";
import { refreshAIAssistantAccessToken } from "./aiAssistantAuth";
import { createSSEParser, PlaygroundStreamError } from "./aiAssistantSSE";

function abortIfNeeded(signal) {
  if (signal?.aborted) throw new DOMException("Request aborted", "AbortError");
}

const stateCodes = new Set(["assistant_not_configured", "assistant_disabled", "assistant_not_provisioned",
  "assistant_unsynchronized", "runtime_not_enabled"]);

export async function streamAIAssistantPlayground({ message, conversationId = null, signal, onEvent }) {
  const request = async () => {
    abortIfNeeded(signal);
    try { return await fetch(`${params.aiAssistantUrl}playground/chat/stream/`, {
      method: "POST", credentials: "omit", signal,
      headers: { "Content-Type": "application/json", Accept: "text/event-stream, application/json",
        Authorization: `Bearer ${localStorage.getItem("accessToken") || ""}` },
      body: JSON.stringify({ message, conversation_id: conversationId }),
    }); } catch (error) {
      if (error.name === "AbortError") throw error;
      throw new PlaygroundStreamError("B-Connect could not be reached. Please try again.");
    }
  };
  let response = await request();
  if (response.status === 401 && localStorage.getItem("refreshToken")) {
    await response.body?.cancel();
    try { await refreshAIAssistantAccessToken(); } catch {
      abortIfNeeded(signal);
      throw new PlaygroundStreamError("Your session expired. Please sign in again.", "unauthorized");
    }
    abortIfNeeded(signal);
    response = await request(); // A single replay; never replay an accepted turn.
  }
  if (!response.ok) {
    let payload;
    try { payload = await response.json(); } catch { payload = {}; }
    abortIfNeeded(signal);
    const messages = {
      401: "Your session expired. Please sign in again.",
      403: "Only organisation Owners can use Playground.",
      404: "The Assistant or conversation is unavailable. Check Settings or start a new conversation.",
      409: "The Assistant or conversation is unavailable. Check Settings or start a new conversation.",
      422: "This Playground request could not be accepted. Start a new conversation.",
      429: "The AI service is busy. Please try again shortly.",
      504: "The AI response timed out. Please try again.",
    };
    throw new PlaygroundStreamError(messages[response.status] || "Playground is unavailable. Please try again shortly.",
      stateCodes.has(payload?.code) ? payload.code : response.status === 403 ? "forbidden" : "http_error");
  }
  if (response.headers.get("Content-Type")?.split(";")[0].trim().toLowerCase() !== "text/event-stream" || !response.body) {
    await response.body?.cancel();
    throw new PlaygroundStreamError("B-Connect returned an invalid streaming response.", "invalid_stream");
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let started = false;
  let completed = false;
  const parser = createSSEParser((event, data) => {
    if (completed || (event === "start" && started) || (event !== "start" && event !== "error" && !started)) {
      throw new PlaygroundStreamError("The AI response sequence was invalid. Start a new conversation.", "invalid_stream");
    }
    if (event === "error") throw new PlaygroundStreamError("The AI response could not be completed. Try again or start a new conversation.");
    if (event === "start") started = true;
    if (event === "completed") completed = true;
    onEvent(event, data);
  });
  const abortReader = () => { reader.cancel().catch(() => {}); };
  signal?.addEventListener("abort", abortReader, { once: true });
  try {
    while (!completed) {
      abortIfNeeded(signal);
      const { value, done } = await reader.read();
      abortIfNeeded(signal);
      if (done) {
        parser.push(decoder.decode());
        parser.finish();
        if (!completed) throw new PlaygroundStreamError("The AI response ended early. Try again or start a new conversation.");
        break;
      }
      parser.push(decoder.decode(value, { stream: true }));
    }
  } catch (error) {
    if (error.name === "AbortError" || error instanceof PlaygroundStreamError) throw error;
    throw new PlaygroundStreamError("The AI response was interrupted. Try again or start a new conversation.");
  } finally {
    signal?.removeEventListener("abort", abortReader);
    try { await reader.cancel(); } catch { /* An already closed/aborted stream needs no further work. */ }
    reader.releaseLock();
  }
}
